/**
 * Escala de empilhamento das camadas flutuantes.
 *
 * ## Por que existir
 *
 * Os valores de `z-index` do produto foram escolhidos um a um, cada um só
 * grande o bastante para resolver o sintoma da vez: `z-50` no overlay do
 * `ReusableModal` no desktop, `z-[100]` na folha inferior do mobile, `z-50` no
 * visualizador de tela cheia, `z-50` em todo popover. Valores iguais não
 * empilham — quem monta por último ganha —, e foi assim que a barra de
 * ferramentas da imagem passou a aparecer **por cima** do diálogo de recorte.
 *
 * Aqui os números deixam de ser opinião de cada arquivo e passam a expressar
 * uma ordem única. Cada degrau tem 10 de folga para um caso pontual caber no
 * meio sem que ninguém precise inventar um `z-[9999]`.
 *
 * ## A ordem, de baixo para cima
 *
 * | degrau            | valor | quem mora aqui                                    |
 * | ----------------- | ----- | ------------------------------------------------- |
 * | `Z_OVERLAY`       | 100   | `ReusableModal` (desktop e folha do mobile)       |
 * | `Z_OVERLAY_NESTED`| 130   | modal aberto de dentro de outro modal             |
 * | `Z_OVERLAY_TOP`   | 140   | confirmação/recorte por cima do modal aninhado    |
 * | `Z_FLOATING`      | 150   | superfícies ancoradas: popover, dropdown, tooltip, menu de contexto, barra flutuante de seleção |
 * | `Z_FLOATING_MENU` | 160   | combobox de barra (`/`, `@`, `:`)                 |
 * | `Z_IMMERSIVE`     | 170   | visualizador de imagem em tela cheia              |
 * | `Z_TOAST`         | 180   | avisos                                            |
 *
 * ## As duas decisões que não são óbvias
 *
 * **Superfície ancorada fica acima de modal.** Um popover, um dropdown ou um
 * tooltip nasce preso a um elemento que pode estar *dentro* de um modal — se
 * ficasse abaixo, todo select dentro de modal sumiria. Por isso `Z_FLOATING`
 * está acima de `Z_OVERLAY_TOP`, e por isso **z-index nunca vai resolver**
 * sozinho o caso de uma barra ancorada a um nó que ficou atrás de um diálogo:
 * essa barra tem de **fechar**, não descer (ver `media-popover.tsx`).
 *
 * **Tela cheia fica acima de superfície ancorada.** O visualizador cobre a
 * janela inteira e não hospeda nada ancorado — o único controle dele é filho
 * dele. É o topo do conteúdo; só aviso passa por cima.
 *
 * ## Por que strings de classe, e não tokens do `tailwind.config`
 *
 * `cn` resolve conflito de `z-index` pelo `tailwind-merge`, que só reconhece
 * `z-<número>` e `z-[arbitrário]`. Um token nomeado (`z-overlay`) não seria
 * reconhecido como classe de `z-index`, então um `className` do chamador
 * deixaria de sobrescrever o padrão e a ordem passaria a depender da ordem do
 * CSS gerado — exatamente o tipo de acidente que esta escala existe para
 * acabar.
 *
 * ## O detalhe que muda a leitura de todos os números
 *
 * No layout do creator existe `<div id="reusable-modal-root" class="relative
 * z-50" />`, e é para lá que o `ReusableModal` do desktop é portado por padrão.
 * `position: relative` com `z-index` cria contexto de empilhamento: os degraus
 * de dentro (100, 130, 140) ordenam os modais **entre si**, mas o conjunto
 * inteiro vale 50 diante do resto da página. É por isso que popover portado
 * para o `body` sempre apareceu por cima de modal — e por isso um diálogo que
 * precisa escapar dessa caixa pede `containerId="body"` (é o caso do recorte
 * de imagem).
 *
 * ## O que ainda não está aqui
 *
 * `components/ui/{dialog,alert-dialog,sheet}.tsx` continuam em `z-50`: são
 * overlays próprios, com chamadores que já compensam o valor com `z-[60]`,
 * `z-[130]` ou `z-[9999]`. Mexer neles mudaria telas que não têm nada a ver
 * com esta correção. O invariante que importa — ancorado acima de overlay —
 * vale mesmo assim, porque 150 é maior que qualquer um deles.
 */

/** Overlay e painel do `ReusableModal`, no desktop e na folha do mobile. */
export const Z_OVERLAY = "z-[100]";

/** Modal aberto de dentro de outro modal (roteiro, detalhe de task). */
export const Z_OVERLAY_NESTED = "z-[130]";

/** Confirmação destrutiva ou recorte por cima de um modal aninhado. */
export const Z_OVERLAY_TOP = "z-[140]";

/** Popover, dropdown, tooltip, menu de contexto, barra flutuante de seleção. */
export const Z_FLOATING = "z-[150]";

/** Combobox de barra (`/`, `@`, `:`) — acima da barra flutuante de seleção. */
export const Z_FLOATING_MENU = "z-[160]";

/** Visualizador de imagem em tela cheia. */
export const Z_IMMERSIVE = "z-[170]";

/** Avisos — sempre visíveis, inclusive sobre tela cheia. */
export const Z_TOAST = "z-[180]";
